import removeRedactedContentfulArray from 'utils/removeRedactedContentfulArray'
import {
  getCurrentProductFromUrl,
  getInitialSelectedSchool,
  getRelevantCourseList,
} from './dataUtils'
import {
  INITIAL_COURSE,
  MODAL_CONTENT_TYPES,
  MODAL_FLOW_TYPES,
} from './constants'
import {
  elementaryV2ContentfulMappings,
  middleSchoolV2ContentfulMappings,
} from 'utils/product/contentfulMappings'

/**
 * Process grades for a single site by reordering based on contentful courses,
 * filtering, sorting, and converting to display names.
 *
 * @param {Object} options - Processing options
 * @param {string[]} options.originalGrades - The original grades array from the site
 * @param {Object[]} options.contentfulCourses - Contentful courses with displayTitle
 * @param {Object} options.filterConfig - Configuration with filter/sort/convert functions
 * @returns {string[]} - Processed display grades
 */
export const processSiteGrades = ({
  originalGrades = [],
  contentfulCourses = [],
  filterConfig = {},
}) => {
  // Clone to avoid mutating the original
  const grades = [...originalGrades]
  const sortedGrades = []

  // Filter contentful courses based on context (classes vs small groups)
  // For classes: only use individual grade courses (K, 1-8), exclude tier2 and training
  // For small groups: only use tier2 curriculum courses
  // Use explicit flag instead of fragile function name matching (which fails after minification)
  const isSmallGroup = filterConfig.isSmallGroup === true

  // For classes: filter to individual grade courses using displayTitle/internalTitle
  // For small groups: filter to tier2 curriculum courses
  const relevantContentfulCourses = contentfulCourses.filter(course => {
    if (!course) return false

    if (isSmallGroup) {
      // For small groups: only tier2 curriculum courses
      // Check name (if exists), internalTitle, or displayTitle
      if (course.name && course.name.includes('_curriculum_tier2')) return true
      const internalTitle = course.internalTitle?.toLowerCase() || ''
      if (internalTitle.includes('tier2')) return true
      const displayTitle = course.displayTitle?.toLowerCase() || ''
      return (
        (displayTitle.includes('k-2') ||
          displayTitle.includes('3-5') ||
          displayTitle.includes('6-8')) &&
        !displayTitle.match(/^grade [k1-8]$/)
      )
    } else {
      // For classes: only individual grades (K, 1-8), exclude tier2 and training
      // Check name first (if exists from userContext adapter)
      if (course.name) {
        const name = course.name.toLowerCase()
        return (
          (name === 'k' || /^[1-8]$/.test(name)) &&
          !name.includes('tier2') &&
          !name.includes('training')
        )
      }
      // Check displayTitle for individual grades
      const displayTitle = course.displayTitle?.toLowerCase() || ''
      if (displayTitle === 'grade k' || /^grade [1-8]$/.test(displayTitle)) {
        // Make sure it's not tier2 or training
        const internalTitle = course.internalTitle?.toLowerCase() || ''
        return (
          !internalTitle.includes('tier2') &&
          !internalTitle.includes('training')
        )
      }
      return false
    }
  })

  // Reorder grades using contentful order (only matching with relevant courses)
  // Use mappings to derive courseName for contentful courses
  relevantContentfulCourses.forEach(contentfulCourse => {
    let index = -1
    let matchedGrade = null

    if (isSmallGroup) {
      // For small groups: match by courseName derived from mappings
      // Derive courseName from displayTitle or internalTitle
      let courseName = contentfulCourse.name
      if (!courseName && contentfulCourse.displayTitle) {
        const mappings = [
          ...elementaryV2ContentfulMappings,
          ...middleSchoolV2ContentfulMappings,
        ]
        const match = mappings.find(
          m => m.displayTitle === contentfulCourse.displayTitle,
        )
        if (match) courseName = match.courseName
      }
      if (courseName) {
        index = grades.findIndex(grade => grade === courseName)
        matchedGrade = courseName
      }
    } else {
      // For classes: match by grade value (e.g., "K" matches "Grade K")
      // Extract grade from displayTitle (e.g., "Grade K" -> "K", "Grade 1" -> "1")
      const displayTitle = contentfulCourse.displayTitle || ''
      const gradeMatch = displayTitle.match(/^Grade (K|[1-8])$/i)
      const isKindergartenTitle = displayTitle.toLowerCase() === 'kindergarten'

      let gradeValue = null
      if (gradeMatch) {
        gradeValue = gradeMatch[1]
      } else if (isKindergartenTitle) {
        gradeValue = 'K'
      }

      if (gradeValue) {
        index = grades.findIndex(
          grade => String(grade).toLowerCase() === gradeValue.toLowerCase(),
        )
        matchedGrade = filterConfig.convertGradeToDisplayValue
          ? filterConfig.convertGradeToDisplayValue(gradeValue) ||
            filterConfig.convertGradeToDisplayValue(displayTitle)
          : displayTitle
      }
    }

    if (index !== -1 && matchedGrade) {
      grades.splice(index, 1)
      sortedGrades.push(matchedGrade)
    }
  })

  // Concat any original grade remaining
  // For classes: if grades weren't matched with contentful courses, they're still raw (K, 1, 2, etc.)
  // We need to convert them to display format
  const allGrades = sortedGrades.concat(grades)

  // Filter grades using the configured filter function
  const filteredGrades = filterConfig.filterFunction
    ? filterConfig.filterFunction(allGrades)
    : allGrades

  // Convert grades to display format BEFORE sorting
  // This is critical because sort functions expect display format (e.g., "Grade K" not "K")
  // For classes: grades might be raw (K, 1, 2) and need conversion (Grade K, Grade 1, Grade 2)
  // For small groups: grades are already in tier2 format (k-2_curriculum_tier2) and need conversion (Grade K-2)
  const gradesForSorting = filteredGrades.map(grade => {
    if (filterConfig.convertGradeToDisplayValue) {
      const converted = filterConfig.convertGradeToDisplayValue(grade)
      return converted || grade
    }
    return grade
  })

  // Sort using the configured sort function (now operating on display format)
  const sortedFilteredGrades = filterConfig.sortFunction
    ? filterConfig.sortFunction(gradesForSorting)
    : gradesForSorting

  // Grades are already in display format, so return them as-is
  const displayGrades = sortedFilteredGrades

  return displayGrades
}

/**
 * Process all sites with grades, applying contentful ordering, filtering, and display conversion.
 * Site-centric rename (was processAllLicenseGrades).
 *
 * @param {Object} options - Processing options
 * @param {Object[]} options.sitesWithGrades - Array of sites with grades property
 * @param {Object[]} options.contentfulCourses - Contentful courses with displayTitle
 * @param {Object} options.filterConfig - Configuration with filter/sort/convert functions
 * @returns {Object[]} - Sites with processed grades (mutates in place)
 */
export const processAllSiteGrades = ({
  sitesWithGrades = [],
  contentfulCourses = [],
  filterConfig = {},
}) => {
  // Clone sitesWithGrades to avoid mutating the original Redux state
  const clonedSites = sitesWithGrades.map(site => ({
    ...site,
    grades: [...(site.grades || [])],
  }))

  clonedSites.forEach(siteWithGrade => {
    siteWithGrade.grades = processSiteGrades({
      originalGrades: siteWithGrade.grades,
      contentfulCourses,
      filterConfig,
    })
  })

  return clonedSites
}

// Helper function to create state updater that handles side effects
export const createStateUpdater = (setState, sideEffectsRef) => {
  return (newState, sideEffects = {}) => {
    setState(newState)
    sideEffectsRef.current = sideEffects
  }
}

export const handleResetFocus = props => {
  props.setFocus && props.setFocus()
}

export const resetState = (state, props, filterConfig = {}) => {
  const newState = {
    isFormValid: false,
    inputErrors: {
      className: {
        errors: null,
      },
      selectedGrade: '',
    },
    courseInstanceName: props.courseInstanceName || '',
    sitesWithGrades: [],
    modalContent: null,
    modalFlow: null,
    selectedCourse: INITIAL_COURSE,
    selectedSchool: getInitialSelectedSchool(props, filterConfig),
    submitAttempts: 0,
  }

  return {
    newState,
    sideEffects: {
      handleResetFocus: true,
    },
  }
}

export const clickCreateSideEffect = (sites, handleSiteChange) => {
  const hasNoSites = !Array.isArray(sites) || sites.length === 0
  const hasMoreThanOneSite = Array.isArray(sites) && sites.length > 1

  if (hasNoSites || hasMoreThanOneSite) {
    return
  }

  // If only one site, auto-select that site
  const e = { value: sites[0].siteName }
  handleSiteChange(e, sites)
}

export const createHandleCreate = filterConfig => (state, props) => {
  const { contentfulCourses, userContext } = props
  const { selectedCourse, selectedSchool } = state || {}

  const availableContentfulCourses =
    removeRedactedContentfulArray(contentfulCourses) || []

  let updatedSelectedCourse = { ...selectedCourse }
  let updatedSelectedGrade = state.selectedGrade || ''

  // Use userContext to find the right site and program for auto-selection
  if (userContext && selectedSchool) {
    const { siteId } = selectedSchool
    const { sites } = userContext

    // Find the selected site in userContext
    const selectedSite = sites?.find(site => site.siteId === siteId)

    if (selectedSite && selectedSite.programs) {
      // Get the current product from URL to determine the program key
      const currentProduct = getCurrentProductFromUrl(props.pathName)
      let programKey = currentProduct?.lmsKeyV2 || 'k5SelDigital' // default fallback

      // TODO: hack to fix lmskey's uppercase first letter. Unknown why this is happening.
      if (programKey === 'MspDigital') {
        programKey = 'mspDigital'
      }

      if (programKey === 'K5SelDigital') {
        programKey = 'k5SelDigital'
      }

      // Get the program from the selected site
      const program = selectedSite.programs[programKey]

      if (program && program.courses) {
        // Use explicit flag instead of fragile function name matching
        const isSmallGroupContext = filterConfig.isSmallGroup === true
        // Use the unified getRelevantCourseList function
        const relevantCourses = filterConfig.getRelevantCourseList
          ? filterConfig.getRelevantCourseList(program.courses)
          : getRelevantCourseList(program.courses, isSmallGroupContext)

        // Auto-select if there's only one relevant course available
        if (relevantCourses.length === 1) {
          const course = relevantCourses[0]
          updatedSelectedCourse.id = course.name
          updatedSelectedCourse.name = course.name

          // Convert curriculum type to display name for small groups
          if (filterConfig.convertGradeToDisplayValue) {
            updatedSelectedGrade =
              filterConfig.convertGradeToDisplayValue(course.name) ||
              course.name
          }
        }
      }
    }
  }

  // Use explicit flag instead of fragile function name matching
  const isSmallGroup = filterConfig?.isSmallGroup === true

  // Get sites with grades from appropriate selector (grades already attached)
  const sitesWithGrades = isSmallGroup
    ? props.allSitesWithGradesForSmallGroups || []
    : props.allSitesWithGradesForClasses || []

  // Process grades: reorder by contentful, filter, sort, and convert to display names
  // processAllSiteGrades now clones the array to avoid mutating Redux state
  const processedSitesWithGrades = processAllSiteGrades({
    sitesWithGrades,
    contentfulCourses: availableContentfulCourses,
    filterConfig,
  })

  // If selectedSchool is prefilled but doesn't have grades, populate them from processedSitesWithGrades
  // OR if selectedSchool is null/empty but we have processedSitesWithGrades, select the first site
  let updatedSelectedSchool = selectedSchool
  if (
    selectedSchool &&
    selectedSchool.siteId &&
    (!selectedSchool.grades || selectedSchool.grades.length === 0)
  ) {
    // Match by siteId and siteName (from text property)
    const siteForSchool = processedSitesWithGrades.find(
      site =>
        site.siteId === selectedSchool.siteId &&
        site.siteName === selectedSchool.text,
    )
    if (siteForSchool) {
      // Grades from sitesWithGrades should already be sorted by processAllSiteGrades
      // But verify they're in the correct order
      const gradesFromSite = siteForSchool.grades || []
      updatedSelectedSchool = {
        ...selectedSchool,
        id: siteForSchool.siteId.toString(), // Use siteId as id
        siteId: siteForSchool.siteId,
        grades: gradesFromSite,
      }
    }
  } else if (
    selectedSchool &&
    selectedSchool.siteId &&
    selectedSchool.grades &&
    selectedSchool.grades.length > 0
  ) {
    // If selectedSchool already has grades, they might be raw tier2 course names
    // Convert them to display format using filterConfig.convertGradeToDisplayValue
    // Then sort them using filterConfig.sortFunction to ensure proper ordering (e.g., Grade K first)
    if (filterConfig.convertGradeToDisplayValue) {
      const convertedGrades = selectedSchool.grades.map(grade =>
        filterConfig.convertGradeToDisplayValue(grade),
      )
      // Sort the converted grades to ensure proper ordering (e.g., Grade K first)
      const sortedConvertedGrades = filterConfig.sortFunction
        ? filterConfig.sortFunction(convertedGrades)
        : convertedGrades
      updatedSelectedSchool = {
        ...selectedSchool,
        grades: sortedConvertedGrades,
      }
    }
  } else if (
    (!selectedSchool || !selectedSchool.siteId) &&
    processedSitesWithGrades.length > 0
  ) {
    // If no school is selected but we have sites with grades, select the first one
    // This handles the case where getInitialSelectedSchool returned null/empty
    const firstSite = processedSitesWithGrades[0]
    updatedSelectedSchool = {
      siteId: firstSite.siteId,
      siteName: firstSite.siteName,
      grades: firstSite.grades,
      id: firstSite.siteId.toString(),
      text: firstSite.siteName, // For backward compatibility with school selector
    }
  }

  const newState = {
    ...state,
    sitesWithGrades: processedSitesWithGrades,
    modalContent: MODAL_CONTENT_TYPES.Create,
    modalFlow: MODAL_FLOW_TYPES.Create,
    previous: state.modalContent,
    selectedCourse: updatedSelectedCourse,
    selectedGrade: updatedSelectedGrade,
    selectedSchool: updatedSelectedSchool,
  }

  // Only trigger side effects if we didn't auto-select a course/grade
  // This prevents side effects from clearing the auto-selected values
  const hasAutoSelectedCourse =
    updatedSelectedCourse.id && updatedSelectedCourse.id !== selectedCourse?.id
  const hasAutoSelectedGrade =
    updatedSelectedGrade && updatedSelectedGrade !== state.selectedGrade

  return {
    newState,
    sideEffects: {
      // Only trigger clickCreateSideEffect if we didn't auto-select anything
      clickCreateSideEffect: !hasAutoSelectedCourse && !hasAutoSelectedGrade,
    },
  }
}

export const handleError = (passedError, state) => {
  const { modalContent: previous, submitAttempts = 0 } = state || {}
  const newState =
    submitAttempts < 2
      ? {
          ...state,
          modalContent: MODAL_CONTENT_TYPES.Error,
          submitAttempts: submitAttempts + 1,
          previous,
        }
      : {
          ...state,
          modalContent: MODAL_CONTENT_TYPES.ErrorDetails,
          error: passedError.message,
          submitAttempts,
          previous,
        }

  return { newState }
}

const getCreateClassSideEffects = () => ({
  deletingCourseCreatedStatus: true,
})

const getSelectedGradeLabel = ({ programConfig, selectedGrade }) =>
  programConfig.convertGradeNameToLabel(selectedGrade)

const resolveProgramKey = ({ data, desiredKey }) => {
  if (!data || !desiredKey) return null
  if (Object.prototype.hasOwnProperty.call(data, desiredKey)) return desiredKey

  const desiredLower = desiredKey.toLowerCase()
  return (
    Object.keys(data).find(key => key.toLowerCase() === desiredLower) || null
  )
}

const getNewCourseFromResponse = ({
  responseData,
  desiredKey,
  errorMessage,
}) => {
  if (!responseData) {
    throw new Error(`${errorMessage}: ${JSON.stringify(responseData)}`)
  }

  const resolvedKey = resolveProgramKey({ data: responseData, desiredKey })
  return resolvedKey ? responseData?.[resolvedKey]?.siteEducator : null
}

const getCourseCentricCreatePayload = ({
  programConfig,
  selectedGrade,
  selectedSchool,
  classTitle,
}) => {
  const selectedGradeLabel = getSelectedGradeLabel({
    programConfig,
    selectedGrade,
  })
  const getCourseCodeFromGradeLabel =
    programConfig.getCourseCodeFromGradeLabel ||
    programConfig.getDwbCourseCodeFromGradeLabel
  const courseCode = getCourseCodeFromGradeLabel?.(selectedGradeLabel)
  const { siteId } = selectedSchool || {}
  const contentGroupCode =
    programConfig.getContentGroupCode?.(selectedGradeLabel) ||
    programConfig.contentGroupCode

  if (!courseCode || siteId == null || !contentGroupCode) {
    throw new Error('Program does not have the selected grade')
  }

  return {
    courseCode,
    contentGroupCode,
    siteId,
    classTitle,
  }
}

const isCourseCentricProgramConfig = programConfig =>
  Boolean(
    (programConfig?.contentGroupCode || programConfig?.getContentGroupCode) &&
      (programConfig.getCourseCodeFromGradeLabel ||
        programConfig.getDwbCourseCodeFromGradeLabel),
  )

const createCourseCentricClass = async ({
  state,
  props,
  programConfig,
  classTitle,
  errorMessage,
}) => {
  const { createCourseInstanceByCourse } = props || {}
  const { selectedGrade, selectedSchool } = state || {}
  const selectedGradeLabel = getSelectedGradeLabel({
    programConfig,
    selectedGrade,
  })
  const responseData = await createCourseInstanceByCourse(
    getCourseCentricCreatePayload({
      programConfig,
      selectedGrade,
      selectedSchool,
      classTitle,
    }),
  )

  const desiredKey =
    programConfig.getInstructorResponseKey?.(selectedGradeLabel) ||
    programConfig.instructorResponseKey ||
    getCurrentProductFromUrl(props.pathName)?.lmsKeyV2

  return getNewCourseFromResponse({
    responseData,
    desiredKey,
    errorMessage,
  })
}

const getProgramFlexUserContext = ({ state, props, programConfig }) => {
  const { userContext } = props || {}
  const { selectedGrade, selectedSchool } = state || {}
  const { siteId } = selectedSchool || {}
  const { sites } = userContext || {}
  const selectedSite = sites?.find(site => site.siteId === siteId) || {}
  const selectedGradeLabel = getSelectedGradeLabel({
    programConfig,
    selectedGrade,
  })
  const programName = programConfig.getProgramName(selectedGradeLabel)
  const program = selectedSite.programs?.[programName] || {}
  const programHasGrade = program.courses?.find(
    course => course.name === selectedGradeLabel,
  )

  if (!programHasGrade) {
    throw new Error('Program does not have the selected grade')
  }

  return {
    sites: [
      {
        siteId: selectedSite.siteId,
        claims: selectedSite.claims,
        programs: {
          [programName]: {
            ...(program.grades ? { grades: program.grades } : {}),
            ...(program.courses
              ? { courses: [{ name: selectedGradeLabel, version: null }] }
              : {}),
            preferences: program.preferences || {},
          },
        },
      },
    ],
  }
}

const createProgramFlexCourse = async ({
  state,
  props,
  programConfig,
  classTitle,
  errorMessage,
}) => {
  const { createCourseInstanceV2ProgramFlex } = props || {}
  const responseData = await createCourseInstanceV2ProgramFlex(
    getProgramFlexUserContext({ state, props, programConfig }),
    classTitle,
  )
  const desiredKey = getCurrentProductFromUrl(props.pathName)?.lmsKeyV2

  return getNewCourseFromResponse({
    responseData,
    desiredKey,
    errorMessage,
  })
}

const getSiteChangeNavigationResult = ({ state, props }) => {
  const selectedSiteId = state?.selectedSchool?.siteId
  const currentSiteId = props?.currentSite?.siteId

  if (!selectedSiteId || selectedSiteId === currentSiteId) return null

  const currentUrl = window.location.href
  const url = new URL(currentUrl)
  url.searchParams.set('site', selectedSiteId)
  const finalPath = `${url.pathname}${url.search}${url.hash}`

  try {
    window.location.replace(finalPath)
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(
      '[CreateInstanceShared] window.location.replace failed',
      error,
    )
    props?.setAppNotLoadingState && props.setAppNotLoadingState()
    window.location.href = finalPath
  }

  return {
    newState: { ...state },
    sideEffects: getCreateClassSideEffects(),
  }
}

const getPostCreateResult = ({ state, props, newCourse }) => {
  try {
    const navigationResult = getSiteChangeNavigationResult({ state, props })
    if (navigationResult) return navigationResult
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[CreateInstanceShared] Post-create URL update failed', error)
  }

  // TODO: LEARN-23339 NEED TO FIGURE OUT HOW TO FAIL if newCourse does not have appropriate fields. This set state is flakey
  return {
    newState: {
      ...state,
      selectedCourse: {
        id: newCourse?.registration_id,
        name: newCourse?.class_title,
      },
      modalContent: null,
      modalFlow: null,
      courseInstanceName: newCourse?.class_title,
      previous: state?.modalContent,
    },
    sideEffects: getCreateClassSideEffects(),
  }
}

export const createHandleSubmit = (
  programConfig,
  errorMessage,
  errorLogMessage,
) => async (state, props) => {
  try {
    const classTitle = state?.courseInstanceName?.trim() || ''
    const newCourse = isCourseCentricProgramConfig(programConfig)
      ? await createCourseCentricClass({
          state,
          props,
          programConfig,
          classTitle,
          errorMessage,
        })
      : await createProgramFlexCourse({
          state,
          props,
          programConfig,
          classTitle,
          errorMessage,
        })

    return getPostCreateResult({ state, props, newCourse })
  } catch (err) {
    console.error(errorLogMessage, err)
    return handleError(err, state)
  }
}

export const goBackOneModal = state => {
  const newState = {
    ...state,
    modalContent: state.previous,
    previous: state.modalContent,
  }

  return { newState }
}

export const handleClassNameChange = (e, state) => {
  const { target } = e || {}
  const { name, value } = target || {}
  const newState = { ...state, [name]: value }

  return { newState, sideEffects: { validateForm: true } }
}

export const handleGradeChange = (e, courses = [], state) => {
  const { option, target } = e || {}
  const { name, value } = target || {}
  const selectedGradeValue = value
  const selectedGradeOption = option || selectedGradeValue
  const matchedCourse = courses.find(course => {
    const displayTitle = course.displayTitle || ''
    if (!displayTitle || !selectedGradeOption) return false
    if (displayTitle === selectedGradeOption) return true
    if (
      selectedGradeOption.toLowerCase() === 'kindergarten' &&
      displayTitle.toLowerCase().includes('grade k')
    ) {
      return true
    }
    return false
  })
  const { id: matchedCourseId } = matchedCourse || {}

  const newState = {
    ...state,
    [name]: {
      name: selectedGradeOption,
      id: matchedCourseId ?? null,
    },
    selectedGrade: selectedGradeValue,
  }

  return { newState, sideEffects: { validateForm: true } }
}

// Site-centric helper: find site by siteName (replaces license-based lookup)
const getSiteFromSchoolOptionText = (optionText, sites = []) => {
  if (!optionText || typeof optionText !== 'string') {
    return null
  }

  return sites.find(site => site.siteName === optionText) || null
}

const createSelectedSchool = matchedSite => {
  return matchedSite ? { ...matchedSite } : {}
}

// Site-centric handler for site selection in create modal
export const createHandleSiteChange = () => (
  e,
  sites = [], // Actually sitesWithGrades, kept parameter name for backward compatibility
  state,
  props,
) => {
  const { value: selectedSiteName } = e || {}

  // Find the matching site from sitesWithGrades by siteName
  const matchedSite =
    typeof selectedSiteName === 'string' && Array.isArray(sites)
      ? getSiteFromSchoolOptionText(selectedSiteName, sites)
      : null

  // matchedSite already has siteId, siteName, and grades from selector
  // No need to look up site from allSites - we already have the site data

  const selectedSchool = createSelectedSchool(matchedSite)

  const newState = {
    ...state,
    selectedSchool,
  }

  return {
    newState,
    sideEffects: {
      validateForm: true,
    },
  }
}

const performUpdate = async (
  updateCourseInstance,
  courseInstanceName,
  props,
) => {
  const { course, fetchInstructorData, userContext } = props
  const { instance: courseInstance } = course || {}

  const courseData = { ...course }
  courseData.title = courseInstanceName
  courseData.instanceId = courseInstance

  const updatedCourse = await updateCourseInstance(courseData)
  const { title: updatedCourseTitle } = updatedCourse || {}

  await fetchInstructorData(userContext)

  return { updatedTitle: updatedCourseTitle }
}
export const createHandleSaveEdit = updateErrorLogMessage => async (
  state,
  props,
) => {
  const { updateCourseInstance } = props || {}
  const { courseInstanceName } = state || {}

  try {
    const result = await performUpdate(
      updateCourseInstance,
      courseInstanceName,
      props,
    )

    const successState = {
      ...state,
      modalContent: null,
      modalFlow: null,
      courseInstanceName: result.updatedTitle,
      previous: state.modalContent,
    }

    return {
      newState: successState,
      sideEffects: {
        deletingCourseCreatedStatus: true,
      },
    }
  } catch (err) {
    console.error(updateErrorLogMessage, err)
    return handleError(err, state)
  }
}

export const createHandleViewClass = viewClassConfig => (state, props) => {
  return viewClassConfig.createViewClassBehavior(state, props)
}

export const createHandleCancel = () => (state, props) => {
  const newState = {
    ...state,
    courseInstanceName: props.courseInstanceName || '',
    inputErrors: {
      className: { errors: null },
      selectedGrade: '',
    },
    isFormValid: false,
    modalContent: null,
    modalFlow: null,
    previous: null,
  }

  return {
    newState,
    sideEffects: {
      handleResetFocus: true,
    },
  }
}

export const handleDeleteSubmit = state => {
  const newState = {
    ...state,
    modalContent: MODAL_CONTENT_TYPES.Delete,
    modalFlow: MODAL_FLOW_TYPES.Delete,
  }

  return { newState }
}

export const handleHideSubmit = state => {
  const newState = {
    ...state,
    modalContent: MODAL_CONTENT_TYPES.HideSmallGroup,
    modalFlow: MODAL_FLOW_TYPES.HideSmallGroup,
  }

  return { newState }
}

export const handleEditSubmit = state => {
  const newState = {
    ...state,
    modalContent: MODAL_CONTENT_TYPES.Edit,
    modalFlow: MODAL_FLOW_TYPES.Edit,
  }

  return {
    newState,
    sideEffects: { validateForm: true },
  }
}

export const createHandleDelete = deleteErrorLogMessage => async (
  state,
  props,
) => {
  const { deleteCourseInstance, course, fetchInstructorData, userContext } =
    props || {}
  const { modalContent } = state || {}
  const { instance: courseInstance } = course || {}

  try {
    await deleteCourseInstance(courseInstance)
    await fetchInstructorData(userContext)

    const newState = {
      ...state,
      courseInstanceName: null,
      modalContent: null,
      previous: modalContent,
    }

    return { newState }
  } catch (err) {
    console.error(deleteErrorLogMessage, err)
    return handleError(err, state)
  }
}

export const createHandleHide = hideErrorLogMessage => async (state, props) => {
  const { finishCourseInstance, course, fetchInstructorData, userContext } =
    props || {}
  const { modalContent } = state || {}
  const { instance: registrationId } = course || {}

  try {
    await finishCourseInstance(registrationId)
    await fetchInstructorData(userContext)

    const newState = {
      ...state,
      courseInstanceName: null,
      modalContent: null,
      previous: modalContent,
    }

    return { newState }
  } catch (err) {
    console.error(hideErrorLogMessage, err)
    return handleError(err, state)
  }
}

export const createValidateForm = checkBlockingValidations => async (
  state,
  props,
) => {
  const validationResult = await checkBlockingValidations(state, props)

  return { newState: { ...state, ...validationResult } }
}
